export interface BrokerInvoiceItem {
  id: number
  description?: string
  rate: number
  hours?: number
  job_site?: string
  date?: string
  contractor?: number
  number_of_loads?: string
  material?: string
  volume?: number
  tag_id: number
  // Required client-provided total (Decimal string, e.g. "123.45")
  total: string
  // Contractor snapshot fields (immutable)
  contractor_first_name?: string
  contractor_last_name?: string
  contractor_email?: string
  contractor_company_name?: string
  contractor_company_address?: string
}

export interface DriverInvoiceItem {
  id: number
  tag_id: number
  description?: string
  rate: number
  hours?: number
  job_site?: string
  date?: string
  truck_number?: string
  material?: string
  volume?: number
  number_of_loads?: string
  broker?: number
  broker_fee?: number
  // Required client-provided total (Decimal string, e.g. "123.45")
  total: string
  // Broker snapshot fields (immutable)
  broker_first_name?: string
  broker_last_name?: string
  broker_email?: string
  broker_company_name?: string
  broker_company_address?: string
}

export type InvoiceItem = BrokerInvoiceItem | DriverInvoiceItem

export interface BillTo {
  id: number
  email: string
  first_name?: string
  last_name?: string
  phone_number?: string
  company?: unknown
}

export interface Invoice {
  id: number
  company_name: string
  company_address: string
  company_city?: string
  company_country?: string
  bill_to: BillTo
  created_by?: {
    id: number
    first_name: string
    last_name: string
    email: string
  }
  invoice_number: string
  invoice_date: string
  due_date: string
  logo?: string
  items: InvoiceItem[]
  // Decimal string from backend
  total_amount: string
  invoice_type: 'BROKER' | 'DRIVER' | 'SUB_HAULER' | 'CONTRACTOR'
  status: 'UNPAID' | 'PAID' | 'CANCELED' | 'DISPUTED'
  // Additional fields
  summary_description?: string
  purchase_order_number?: string
  payment_terms_text?: string
  how_to_pay?: string
  // Dispute fields
  disputed_by?: number | null
  disputed_at?: string | null
  dispute_reason?: string
  // Bill-to snapshot fields (immutable)
  bill_to_first_name?: string
  bill_to_last_name?: string
  bill_to_email?: string
  bill_to_phone?: string
  bill_to_company_name?: string
  bill_to_company_address?: string
  // Created-by snapshot fields (immutable)
  created_by_first_name?: string
  created_by_last_name?: string
  created_by_email?: string
  created_by_company_name?: string
}

export interface CreateInvoiceData {
  company_name: string
  company_address: string
  bill_to_user_id: number
  invoice_number?: string
  invoice_date: string
  due_date: string
  items: InvoiceItem[]
  invoice_type: 'BROKER' | 'DRIVER' | 'SUB_HAULER' | 'CONTRACTOR'
  summary_description?: string
  purchase_order_number?: string
  payment_terms_text?: string
  how_to_pay?: string
}

export interface UpdateInvoiceData {
  company_name?: string
  company_address?: string
  bill_to_user_id?: number
  invoice_number?: string
  invoice_date?: string
  due_date?: string
  items?: InvoiceItem[]
  invoice_type?: 'BROKER' | 'DRIVER' | 'SUB_HAULER' | 'CONTRACTOR'
  summary_description?: string
  purchase_order_number?: string
  payment_terms_text?: string
  how_to_pay?: string
}

export interface InvoiceStatusUpdate {
  status: 'UNPAID' | 'PAID' | 'CANCELED' | 'DISPUTED'
}

// Helper type guards
export function isBrokerInvoiceItem(
  item: InvoiceItem,
): item is BrokerInvoiceItem {
  return (
    'tag_id' in item && !('truck_number' in item) && !('broker_fee' in item)
  )
}

export function isDriverInvoiceItem(
  item: InvoiceItem,
): item is DriverInvoiceItem {
  return 'tag_id' in item && ('truck_number' in item || 'broker_fee' in item)
}

// Common fields that can be updated
export type CommonInvoiceItemFields =
  | 'id'
  | 'description'
  | 'rate'
  | 'hours'
  | 'job_site'
  | 'date'
  | 'material'
  | 'volume'
  | 'total'
export type BrokerInvoiceItemFields =
  | CommonInvoiceItemFields
  | 'number_of_loads'
  | 'contractor'
export type DriverInvoiceItemFields =
  | CommonInvoiceItemFields
  | 'truck_number'
  | 'broker_fee'
